import React, { useState, useEffect, memo } from 'react';
import { Loader2, ImageIcon } from 'lucide-react';
import { getApiUrl } from '../../utils/config';
import { cn } from '@/lib/utils';

interface AuthenticatedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  alt: string;
  clientId?: string;
}

const imageCache = new Map<string, string>();

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ src, alt, className, clientId, ...props }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    if (!src) {
      setStatus('error');
      return;
    }

    if (imageCache.has(src)) {
      setImageSrc(imageCache.get(src)!);
      setStatus('loaded');
      return;
    }

    let isCancelled = false;

    const loadImage = async () => {
      setStatus('loading');
      try {
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        };
        if (clientId) {
          headers['X-Client-ID'] = clientId;
        }

        const isAbsoluteUrl = src.startsWith('http');
        const imageUrl = isAbsoluteUrl ? src : `${getApiUrl()}${src}`;
        
        // For external URLs (like S3 presigned), we might not need auth headers.
        // This logic assumes internal URLs need auth, external ones don't.
        const fetchOptions = isAbsoluteUrl ? {} : { headers };

        const response = await fetch(imageUrl, fetchOptions);

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        if (!isCancelled) {
          imageCache.set(src, blobUrl);
          setImageSrc(blobUrl);
          setStatus('loaded');
        }
      } catch (error) {
        if (!isCancelled) {
          console.warn('Image loading failed, showing placeholder:', error);
          setStatus('error');
        }
      } 
    };

    loadImage();

    return () => {
      isCancelled = true;
      // Revoking object URL is tricky with cache. A better cache management strategy is needed for long-running apps.
    };
  }, [src, clientId]);

  if (status === 'loading') {
    return (
      <div className={cn("flex items-center justify-center bg-muted/50", className)}>
        <Loader2 className="h-1/4 w-1/4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (status === 'error' || !imageSrc) {
    return (
      <div className={cn("flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 to-purple-500/5 border border-primary/20", className)}>
        <div className="w-8 h-8 bg-gradient-to-br from-primary to-purple-600 rounded-lg flex items-center justify-center mb-2">
          <ImageIcon className="h-4 w-4 text-white" />
        </div>
        <span className="text-xs text-muted-foreground font-medium">AI Generated</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      {...props}
    />
  );
};

export default memo(AuthenticatedImage);
